/*
 * The Seat Plan screen's state, shared by the classic panel and the v3 screen.
 *
 * `lockedSeats` is the derivation the frozen frame leads with: a seat someone has already paid for
 * cannot be moved or deleted, and the count of those is the first thing the frame puts on the page.
 * It comes from occupancy — a row with an `orderCode` is a chair with a buyer's name on it — and
 * nothing else on this screen is allowed to imply otherwise.
 *
 * `zoneStats` joins each zone to the ticket type it prices against, because a zone carries no price
 * of its own. A table zone reports admissions as well as units: `capacity` counts units, so a room
 * that admits 60 people reads 15 without the multiplication, and an organizer seeing only that
 * number concludes something is broken.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type { AdminTicketDto, SeatDto, SeatOccupancyDto, SeatZoneDto } from '../../api';

export interface ZoneStats {
  zone: SeatZoneDto;
  ticket: AdminTicketDto | null;
  /** Units in the zone as the ticket type counts them. */
  capacity: number;
  /** Units with a buyer attached — these are the ones that cannot be moved or removed. */
  sold: number;
  /** People, not units: a table seats `ticket.seats` of them. */
  admits: number;
  price: number;
  isTable: boolean;
}

export interface SeatPlanState {
  zones: SeatZoneDto[];
  tickets: AdminTicketDto[];
  seats: SeatDto[];
  occupancy: SeatOccupancyDto[];
  zoneStats: ZoneStats[];
  /** Every seat across the event that a buyer already holds. */
  lockedSeats: number;
  activeZoneId: string | null;
  setActiveZoneId: (id: string | null) => void;
  /** Ticket types that may still host a zone: seated mode, and not already hosting one. */
  eligibleTickets: AdminTicketDto[];
  /** No ticket type is set to seated at all — the zone builder has nothing to attach to. */
  noSeatedTypes: boolean;
  loading: boolean;
  loadError: string;
  error: string;
  busy: boolean;
  createZone: (input: {
    ticketId: string; code: string; label: string; color: string; unitKind: 'seat' | 'table';
  }) => Promise<void>;
  generateLayout: (zoneId: string, input: {
    rows: number; seatsPerRow: number; rowLabelStyle: 'alpha' | 'numeric'; aisleAfter: number[];
  }) => Promise<void>;
  toggleSeatBlocked: (seat: SeatDto) => Promise<void>;
  /**
   * A zone's colour and how buyers pick seats in it.
   *
   * `selectionMode` is the one that changes what a buyer does: `pick` puts them on the plan and
   * `best_available` has the system choose. The classic panel could set both and the rebuilt
   * screen could set neither, so a zone was whatever it was created as, for ever.
   */
  updateZone: (
    zone: SeatZoneDto,
    patch: { label?: string; color?: string; selectionMode?: 'pick' | 'best_available' },
  ) => Promise<void>;
  reload: () => Promise<void>;
}

export function useSeatPlan(token: string, eventSlug?: string): SeatPlanState {
  const [zones, setZones] = useState<SeatZoneDto[] | null>(null);
  const [tickets, setTickets] = useState<AdminTicketDto[]>([]);
  const [seats, setSeats] = useState<SeatDto[]>([]);
  const [occupancy, setOccupancy] = useState<SeatOccupancyDto[]>([]);
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [z, t, o] = await Promise.all([
        adminApi.listSeatZones(token, eventSlug),
        adminApi.listTickets(token, eventSlug),
        adminApi.getSeatOccupancy(token, eventSlug),
      ]);
      setZones(z);
      setTickets(t);
      setOccupancy(o);
      setActiveZoneId((cur) => cur ?? z[0]?.id ?? null);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดผังที่นั่งไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeZoneId) { setSeats([]); return; }
    adminApi.listSeats(token, activeZoneId, eventSlug).then(setSeats).catch(() => setSeats([]));
  }, [activeZoneId, token, eventSlug, occupancy]);

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const zoneList = zones ?? [];
  const held = occupancy.filter((o) => o.orderCode);

  return {
    zones: zoneList, tickets, seats, occupancy,
    zoneStats: zoneList.map((zone) => {
      const ticket = tickets.find((t) => t.id === zone.ticketId) ?? null;
      const perUnit = ticket?.seats ?? 1;
      const sold = held.filter((o) => o.zoneCode === zone.code).length;
      const capacity = ticket?.capacity ?? 0;
      const isTable = zone.unitKind === 'table';
      return {
        zone, ticket, capacity, sold,
        admits: isTable ? capacity * perUnit : capacity,
        price: ticket?.priceRegular ?? 0,
        isTable,
      };
    }),
    lockedSeats: held.length,
    activeZoneId, setActiveZoneId,
    eligibleTickets: tickets.filter(
      // Only a seated type can host a zone, and only one zone each — the two rules that keep a
      // zone from becoming a second pricing system.
      (t) => t.seatingMode === 'zone_seated' && !zoneList.some((z) => z.ticketId === t.id),
    ),
    noSeatedTypes: tickets.length > 0 && tickets.every((t) => t.seatingMode !== 'zone_seated'),
    loading: zones === null && !loadError,
    loadError, error, busy,
    createZone: (input) => run(() => adminApi.createSeatZone(token, input, eventSlug)),
    generateLayout: (zoneId, input) => run(() => adminApi.generateSeatLayout(token, zoneId, input, eventSlug)),
    updateZone: (zone, patch) => run(() => adminApi.updateSeatZone(token, zone.id, patch, eventSlug)),
    toggleSeatBlocked: (seat) => run(() => adminApi.setSeatBlocked(token, seat.id, seat.state !== 'blocked', eventSlug)),
    reload: load,
  };
}
